/**
 * A once-a-second countdown measured against **core's** clock.
 *
 * Every seller auction response carries `serverTime` precisely so a countdown does not
 * depend on the founder's machine. `useApiResource` records `receivedAtMs` — the local
 * instant the response landed — and the difference between the two is the offset between
 * the clocks. Every tick afterwards is local elapsed time plus that offset, so a device
 * minutes out of step still shows the same remaining time as the server.
 *
 * This matters more here than on the public board: past the boundary the desk stops
 * offering the bid form, so a wrong clock would mean refusing a bid core would have taken.
 *
 * Both inputs are plain data, and the current time arrives from a subscribable store, so
 * this hook reads no mutable value during render.
 */
import { useSyncExternalStore } from "react";
import { countdownBetween } from "../lib/countdown";
import { clockSnapshot, subscribeToClock } from "../lib/clock";
import type { Countdown } from "../lib/countdown";

/**
 * @param targetIso      the instant being counted down to, or `null` when there is none
 * @param serverTimeIso  `serverTime` from the response that produced `targetIso`
 * @param receivedAtMs   the local instant that response arrived
 */
export function useCountdown(
  targetIso: string | null,
  serverTimeIso: string | null,
  receivedAtMs: number | null,
): Countdown | null {
  const nowMs = useSyncExternalStore(subscribeToClock, clockSnapshot);

  if (targetIso === null) return null;
  const targetMs = Date.parse(targetIso);
  if (Number.isNaN(targetMs)) return null;

  // Network latency makes the offset an approximation of a few hundred milliseconds, which
  // is irrelevant to a countdown displayed in seconds. A missing or unparseable server time
  // degrades to the local clock rather than to a broken countdown.
  const serverMs = serverTimeIso === null ? Number.NaN : Date.parse(serverTimeIso);
  const offsetMs =
    Number.isNaN(serverMs) || receivedAtMs === null ? 0 : serverMs - receivedAtMs;

  return countdownBetween(nowMs + offsetMs, targetMs);
}
